(() => {
  const DEBOUNCE_DELAY_MS = 300;
  const THUMBNAIL_SIZE_PX = 56;
  const PANEL_CLASS = 'c-search-suggest';
  const LINK_SELECTOR = 'a[href]';
  const MODAL_SELECTOR = '.header__search details';
  const VIEW_ALL_TERMS_PLACEHOLDER = '__VERE_TERMS__';

  if (window.cSearchSuggestReady || !window.vereSearchSuggest) return;

  window.cSearchSuggestReady = true;

  const configuration = window.vereSearchSuggest;

  /** 相対URLだけを安全にリンクへ設定する。 */
  const setSafeUrl = (element, url) => {
    if (typeof url !== 'string' || !url.startsWith('/') || url.startsWith('//')) return false;

    element.setAttribute('href', url);
    return true;
  };

  /** 検索URLを生成する。 */
  const createSearchUrl = (action, terms, includeView) => {
    const url = new URL(action, window.location.origin);

    url.searchParams.set('q', terms);
    url.searchParams.set('options[prefix]', 'last');
    if (configuration.productsOnly) url.searchParams.set('type', 'product');
    if (includeView) url.searchParams.set('view', 'c-suggest');

    return `${url.pathname}${url.search}`;
  };

  /** 指定したフォームのサジェストを初期化する。 */
  const initForm = (form) => {
    if (form.dataset.cSearchSuggestInitialized) return;

    const input = form.querySelector('.search__input');
    const field = form.querySelector('.field');
    if (!input || !field) return;

    form.dataset.cSearchSuggestInitialized = 'true';

    const panel = document.createElement('div');
    panel.className = PANEL_CLASS;
    panel.hidden = true;
    panel.setAttribute('role', 'region');
    panel.setAttribute('aria-live', 'polite');
    field.insertAdjacentElement('afterend', panel);

    let abortController;
    let debounceTimer;
    let isComposing = false;

    const closePanel = () => {
      clearTimeout(debounceTimer);
      abortController?.abort();
      panel.hidden = true;
      panel.replaceChildren();
    };

    const addKeywordChips = (container) => {
      if (!configuration.keywords.length) return;

      const chips = document.createElement('div');
      chips.className = 'c-search-suggest__chips';
      configuration.keywords.forEach((keyword) => {
        const chip = document.createElement('a');
        chip.className = 'c-search-suggest__chip';
        chip.textContent = keyword;
        setSafeUrl(chip, createSearchUrl(form.action, keyword, false));
        chips.append(chip);
      });
      container.append(chips);
    };

    const showPopularKeywords = () => {
      panel.replaceChildren();
      if (!configuration.keywords.length) return closePanel();

      const section = document.createElement('div');
      section.className = 'c-search-suggest__section';
      const heading = document.createElement('p');
      heading.className = 'c-search-suggest__heading';
      heading.textContent = configuration.strings.popularHeading;
      section.append(heading);
      addKeywordChips(section);
      panel.append(section);
      panel.hidden = false;
    };

    const addProducts = (products) => {
      const productsSection = document.createElement('div');
      productsSection.className = 'c-search-suggest__products';
      const heading = document.createElement('p');
      heading.className = 'c-search-suggest__heading c-search-suggest__section';
      heading.textContent = configuration.strings.productsHeading;
      productsSection.append(heading);

      products.forEach((product) => {
        const link = document.createElement('a');
        link.className = 'c-search-suggest__product';
        if (!setSafeUrl(link, product.url)) return;

        if (product.image) {
          const image = document.createElement('img');
          image.className = 'c-search-suggest__image';
          image.setAttribute('src', product.image);
          image.alt = '';
          image.width = THUMBNAIL_SIZE_PX;
          image.height = THUMBNAIL_SIZE_PX;
          link.append(image);
        } else {
          const placeholder = document.createElement('span');
          placeholder.className = 'c-search-suggest__placeholder';
          placeholder.setAttribute('aria-hidden', 'true');
          link.append(placeholder);
        }

        const text = document.createElement('span');
        const title = document.createElement('span');
        title.className = 'c-search-suggest__product-title';
        title.textContent = product.title;
        const price = document.createElement('span');
        price.className = 'c-search-suggest__product-price';
        price.textContent = product.price;
        text.append(title, price);
        link.append(text);
        productsSection.append(link);
      });
      panel.append(productsSection);
    };

    const showResults = (data, terms) => {
      panel.replaceChildren();
      const products = Array.isArray(data.products) ? data.products : [];

      if (!products.length) {
        const message = document.createElement('p');
        message.className = 'c-search-suggest__message';
        message.textContent = configuration.strings.noResults;
        panel.append(message);
        const section = document.createElement('div');
        section.className = 'c-search-suggest__section';
        addKeywordChips(section);
        panel.append(section);
        panel.hidden = false;
        return;
      }

      addProducts(products);
      const allResults = document.createElement('a');
      allResults.className = 'c-search-suggest__all';
      allResults.textContent = configuration.strings.viewAll.replace(
        VIEW_ALL_TERMS_PLACEHOLDER,
        data.terms || terms,
      );
      setSafeUrl(allResults, createSearchUrl(form.action, terms, false));
      panel.append(allResults);
      panel.hidden = false;
    };

    const fetchResults = async (terms) => {
      abortController?.abort();
      abortController = new AbortController();

      try {
        const response = await fetch(createSearchUrl(form.action, terms, true), {
          signal: abortController.signal,
          headers: { Accept: 'application/json' },
        });
        if (!response.ok) throw new Error('Search suggestion request failed');

        const data = await response.json();
        if (input.value.trim() !== terms) return;
        showResults(data, terms);
      } catch (error) {
        if (error.name !== 'AbortError') closePanel();
      }
    };

    const queueSearch = () => {
      const terms = input.value.trim();
      clearTimeout(debounceTimer);
      if (!terms) return showPopularKeywords();

      debounceTimer = setTimeout(() => fetchResults(terms), DEBOUNCE_DELAY_MS);
    };

    input.addEventListener('focus', queueSearch);
    input.addEventListener('input', () => {
      if (!isComposing) queueSearch();
    });
    input.addEventListener('compositionstart', () => {
      isComposing = true;
      clearTimeout(debounceTimer);
    });
    input.addEventListener('compositionend', () => {
      isComposing = false;
      queueSearch();
    });
    form.addEventListener('keydown', (event) => {
      if (event.isComposing) return;
      if (event.key === 'Escape') return closePanel();
      if (!['ArrowDown', 'ArrowUp'].includes(event.key) || panel.hidden) return;

      const links = [...panel.querySelectorAll(LINK_SELECTOR)];
      if (!links.length) return;
      const currentIndex = links.indexOf(document.activeElement);

      if (event.key === 'ArrowDown') {
        event.preventDefault();
        links[Math.min(Math.max(currentIndex + 1, 0), links.length - 1)].focus();
        return;
      }

      if (currentIndex < 0) return;
      event.preventDefault();
      if (currentIndex === 0) return input.focus();
      links[currentIndex - 1].focus();
    });
    form.addEventListener('reset', closePanel);
    document.addEventListener('click', (event) => {
      if (!form.contains(event.target)) closePanel();
    });

    const modal = form.closest(MODAL_SELECTOR);
    modal?.addEventListener('toggle', () => {
      if (!modal.open) closePanel();
    });
  };

  const initForms = () => document.querySelectorAll('form[role="search"]').forEach(initForm);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initForms);
  } else {
    initForms();
  }
})();
